import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useInfiniteHandler, useQueryToast } from "hooks";
import { apiClient } from "utils";
import { getDefaultInfinitePageParam } from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  IAcceptOrRejectContractRequest,
  IContractListItemResponse,
  IContractListParams,
  IContractListResponse,
  IContractResponse,
} from "./types";
import i18next from "i18next";

interface IContractsByLabelOptions {
  params?: IContractListParams;
  enabled?: boolean;
  retry?: boolean;
  staleTime?: number; // 5 minutes (data stays fresh)
  refetchOnWindowFocus?: boolean; // Don't refetch when window gains focus
}

export const useGetContractsByLabel = (
  labelId: string,
  options?: IContractsByLabelOptions
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.params?.page,
    options?.params?.itemCount
  );

  return useInfiniteQuery<IContractListItemResponse[], DefaultError>({
    queryKey: ["contacts", labelId, options?.params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: `Contract/label`,
        params: {
          ...options?.params,
          pageCount: itemCount,
          page: pageParam,
          labelId: labelId,
        },
      });

      const contracts = await responseHandler<IContractListResponse>(response, {
        groupName: `useGetContactsListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IContractListItemResponse>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: options?.enabled,
    retry: options?.retry,
    staleTime: options?.staleTime,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export const useGetContracts = (params?: IContractListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IContractListItemResponse[], DefaultError>({
    queryKey: ["contacts", params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: `Contract/Label`,
        params: { ...params, itemCount, page: pageParam },
      });

      const contracts = await responseHandler<IContractListResponse>(response, {
        groupName: `useGetContactsListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IContractListItemResponse>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetContract = (id: string) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IContractResponse, DefaultError>({
    queryKey: ["contract", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Contract/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetContract - ${id}`,
        notFoundPath: "/contracts",
      });
    },
    enabled: !!id,
  });
};

export const useAcceptContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useAcceptContract");
  return useMutation<
    IContractResponse,
    DefaultError,
    IAcceptOrRejectContractRequest
  >({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("Contract.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "Contract/Accept",
        body,
      });

      return responseHandler(response, {
        groupName: "useAcceptContract",
        notFoundPath: "/contracts",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: data.title ?? i18next.t("contracts.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useRejectContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useAcceptContract");
  return useMutation<
    IContractResponse,
    DefaultError,
    IAcceptOrRejectContractRequest
  >({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("Contract.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "Contract/Reject",
        body,
      });

      return responseHandler(response, {
        groupName: "useAcceptReject",
        notFoundPath: "/contracts",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: data.title ?? i18next.t("contracts.what"),
        })
      );
    },
    onError: dismiss,
  });
};
